import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Placeholder from "components/atoms/Placeholder";
import Tooltip from "components/atoms/Tooltip";
import Button from "components/atoms/button";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import { ModalContainer, ModalDescText } from "components/page/modal/styled";
import AbstractModal from "components/page/modal/AbstractModal";
import TagEditModal from "components/page/modal/TagEditModal";

import { useNavigatorTagCreate, useNavigatorTagResourcesCreate, useNavigatorTags } from "hooks/queries/useNavigator";
import { useTagRecommendation } from "hooks/queries/useReference";

const MessageLine = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 24px 0 0 0;
    width: 100%;
`;

const ContentsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 16px 0 40px 0;
    width: 100%;
    min-height: 240px;
    max-height: 320px;
`;

const NormalView = styled.div`
    display: flex;
    padding: 8px;
    width: 100%;
    min-height: 240px;
    max-height: 320px;
    background-color: var(--color-Base1);
    border-radius: 4px;
    overflow: hidden;
`;

const BlankView = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const TagListWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    align-content: flex-start;
    width: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding-right: 4px;
`;

const TagItem = styled.div`
    margin: 0 4px 4px 0;
    padding: 6px;
    max-width: 240px;
    height: 28px;
    background-color: ${(props) => (props.selected ? "var(--color-TagActive)" : "var(--color-Tag)")};
    border: solid 1px transparent;
    border-radius: 2px;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: 120ms ease-in;
    display: flex;
    align-items: center;

    &:hover {
        border: solid 1px var(--color-Key);
    }
`;

const RecommendedTagItem = styled(TagItem)``;

const TagAddButtonWrap = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 16px 0 0 0;
`;

function ReferenceTagEditModal(props) {
    const { exitModal, referenceFile, referenceId, selectedTagIds: initialSelectedTagIds } = props;
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const hasInitializedSelectedTagsRef = useRef(false);
    const [tags, setTags] = useState([]);
    const [showTagAddModal, setShowTagAddModal] = useState(false);

    const tagCreate = useNavigatorTagCreate();
    const tagResourcesCreate = useNavigatorTagResourcesCreate();

    const tagQuery = useNavigatorTags();

    const { setToast } = useToast();

    useEffect(() => {
        if (!hasInitializedSelectedTagsRef.current) {
            setSelectedTagIds(initialSelectedTagIds || []);
            hasInitializedSelectedTagsRef.current = true;
        }
    }, [initialSelectedTagIds]);

    useEffect(() => {
        if (tagQuery.data) {
            setTags(tagQuery.data);
        }
    }, [tagQuery.data]);

    const handleCheck = (tagId) => {
        if (selectedTagIds.includes(tagId)) {
            setSelectedTagIds(selectedTagIds.filter((selectedTag) => selectedTag !== tagId));
        } else {
            setSelectedTagIds([...selectedTagIds, tagId]);
        }
    };

    const applySelectedTag = () => {
        tagResourcesCreate.mutate(
            {
                resources: [{ resource_type: "reference", id: referenceId }],
                tag_ids: selectedTagIds,
                recommended_tags: recommendedTags.filter((tag) => tag.selected).map((tag) => tag.name),
                replace: true,
            },
            {
                onSuccess: () => {
                    exitModal();
                    setToast("선택한 태그가 연결되었습니다.", "info");
                },
                onError: () => {
                    setToast("태그 연결에 실패했습니다. 잠시후 시도해주세요.", "error");
                },
            },
        );
    };

    const [recommendedTags, setRecommendedTags] = useState([]);
    const { mutate: recommendTags, isLoading: isRecommendationLoading } = useTagRecommendation();

    useEffect(() => {
        if (!referenceFile || !referenceId) return;

        // 화면 진입 시 PDF 파일 있으면 태그 추천 실행
        recommendTags(
            { reference_id: referenceId },
            {
                onSuccess: (data) => {
                    setRecommendedTags(
                        data.tag_list.map((tag_name) => {
                            return {
                                name: tag_name,
                                selected: false,
                            };
                        }),
                    );
                },
                onError: (error) => {
                    console.log("recommendation error");
                    console.log(error);
                },
            },
        );
    }, [referenceFile, referenceId, recommendTags]);

    return (
        <AbstractModal
            modalTitle="태그 연결"
            width={480}
            exitModal={exitModal}
            footer={{
                leftBtnText: "취소",
                rightBtnText: "적용",
                leftOnClick: exitModal,
                rightOnClick: applySelectedTag,
            }}
        >
            <ModalContainer>
                <MessageLine>
                    <ModalDescText>참고문헌에 연결할 태그를 선택해주세요.</ModalDescText>
                </MessageLine>

                <TagAddButtonWrap>
                    <Button
                        width={"100px"}
                        height={"24px"}
                        bgColor={"var(--color-Button1)"}
                        hoverBgColor={"var(--color-ButtonHover1)"}
                        fontColor={"var(--color-White)"}
                        buttonText={"새 태그"}
                        onClick={(e) => setShowTagAddModal(true)}
                    >
                        <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                    </Button>
                </TagAddButtonWrap>

                <ContentsWrapper>
                    <NormalView>
                        {tags.length > 0 || referenceFile ? (
                            <TagListWrap>
                                {isRecommendationLoading && (
                                    <>
                                        {["70px"].map((width, index) => (
                                            <Tooltip message={"PDF를 분석 중입니다."} key={"tooltip" + index}>
                                                <RecommendedTagItem key={index}>
                                                    <Placeholder
                                                        style={{ width: width, height: "15px", borderRadius: "1px" }}
                                                    />
                                                </RecommendedTagItem>
                                            </Tooltip>
                                        ))}
                                    </>
                                )}
                                {initialSelectedTagIds.map((tagId, index) => {
                                    const tag = tags.find((tag) => tag.id === tagId);
                                    if (!tag) return null;

                                    return (
                                        <Tooltip
                                            message={
                                                selectedTagIds.includes(tag.id)
                                                    ? "참고문헌에 연결된 태그입니다."
                                                    : "참고문헌에 연결되지 않은 태그입니다."
                                            }
                                            key={"tooltip" + index}
                                        >
                                            <TagItem
                                                key={index}
                                                selected={selectedTagIds.includes(tagId)}
                                                onClick={(e) => handleCheck(tagId)}
                                            >
                                                {recommendedTags.find(
                                                    (recommendedTag) => recommendedTag.name === tag.name,
                                                ) && (
                                                    <Icon
                                                        name={"ai"}
                                                        size={"12"}
                                                        color="var(--color-Black)"
                                                        style={{ marginRight: "4px" }}
                                                    />
                                                )}
                                                <Text variant="body-sm-400" color={"var(--color-Black)"} ellipsis>
                                                    {tag.name}
                                                </Text>
                                            </TagItem>
                                        </Tooltip>
                                    );
                                })}
                                {recommendedTags.length > 0 && (
                                    <>
                                        {recommendedTags
                                            .filter(
                                                (recommendedTag) =>
                                                    !initialSelectedTagIds.some((id) => {
                                                        const tag = tags.find((tag) => tag.id === id);
                                                        return tag && tag.name === recommendedTag.name;
                                                    }),
                                            )
                                            .map((tag, index) => (
                                                <Tooltip
                                                    message={
                                                        tag.selected
                                                            ? "참고문헌에 연결된 태그입니다."
                                                            : "PDF를 분석해 추천된 태그입니다."
                                                    }
                                                    key={"tooltip" + index}
                                                >
                                                    <RecommendedTagItem
                                                        key={JSON.stringify(tag)}
                                                        selected={tag.selected}
                                                        onClick={(e) => {
                                                            const newRecommendedTags = [...recommendedTags];
                                                            const tagIndex = newRecommendedTags.findIndex(
                                                                (t) => t.name === tag.name,
                                                            );
                                                            if (tagIndex !== -1) {
                                                                newRecommendedTags[tagIndex].selected =
                                                                    !newRecommendedTags[tagIndex].selected;
                                                            }

                                                            setRecommendedTags(newRecommendedTags);
                                                        }}
                                                    >
                                                        <Icon
                                                            name={"ai"}
                                                            size={"12"}
                                                            color="var(--color-Black)"
                                                            style={{ marginRight: "4px" }}
                                                        />
                                                        <Text
                                                            variant="body-sm-400"
                                                            color={"var(--color-Black)"}
                                                            ellipsis
                                                        >
                                                            {tag.name}
                                                        </Text>
                                                    </RecommendedTagItem>
                                                </Tooltip>
                                            ))}
                                    </>
                                )}
                                {tags
                                    .filter(
                                        (tag) =>
                                            !initialSelectedTagIds.includes(tag.id) &&
                                            !recommendedTags.some((recommendedTag) => recommendedTag.name === tag.name),
                                    )
                                    .map((tag, index) => (
                                        <Tooltip
                                            message={
                                                selectedTagIds.includes(tag.id)
                                                    ? "참고문헌에 연결된 태그입니다."
                                                    : "참고문헌에 연결되지 않은 태그입니다."
                                            }
                                            key={"tooltip" + index}
                                        >
                                            <TagItem
                                                key={index}
                                                selected={selectedTagIds.includes(tag.id)}
                                                onClick={(e) => handleCheck(tag.id)}
                                            >
                                                <Text variant="body-sm-400" color={"var(--color-Black)"} ellipsis>
                                                    {tag.name}
                                                </Text>
                                            </TagItem>
                                        </Tooltip>
                                    ))}
                            </TagListWrap>
                        ) : (
                            <BlankView>
                                <Text variant="body-sm-400" textAlign={"center"} color={"var(--color-DisabledText)"}>
                                    아직 태그가 없습니다.
                                </Text>
                            </BlankView>
                        )}
                    </NormalView>
                </ContentsWrapper>
                {showTagAddModal && (
                    <TagEditModal
                        exitModal={() => setShowTagAddModal(false)}
                        item={showTagAddModal}
                        mutation={tagCreate}
                        onSuccess={(added) => handleCheck(added.id)}
                    />
                )}
            </ModalContainer>
        </AbstractModal>
    );
}

export default ReferenceTagEditModal;
